export type TaskStatus = 'pending' | 'completed' | 'cancelled';

export type TaskEvent =
  | 'birthday'
  | 'truck_anniversary'
  | 'lead_lost'
  | 'follow_up'
  | 'check_in'
  | 'custom';

/**
 * Communication channels associated with a task. Used both as the user's
 * preference when creating the task (which channels they intend to send) and
 * later to seed the defaults in the completion dialog.
 */
export type TaskChannel = 'email' | 'text';

/**
 * How much of the task the system should run on its own.
 *
 * - `full`: a scheduled function automatically completes the task on the due
 *   date, sending whatever channels the user picked at creation time.
 * - `partial`: the user manually completes the task; the completion dialog
 *   walks them through composing and sending the message(s).
 * - `manual`: the task represents work that can't be done by sending an
 *   email/text. The complete button just marks it done with no dialog.
 */
export type TaskAutomationLevel = 'full' | 'partial' | 'manual';

export const TASK_AUTOMATION_LEVELS: TaskAutomationLevel[] = ['full', 'partial', 'manual'];

/**
 * Snapshot of an email that was sent when completing a task.
 * Stored on the task so the user can review it later.
 */
export interface TaskCompletionEmail {
  recipientEmail: string;
  subject: string;
  html: string;
  /** The mail template key used (e.g. `birthdayEmail`). */
  templateName: string;
  /** Personalized free-text the user typed when composing. */
  personalMessage: string;
  /** Display name of the sender used in the sign-off. */
  senderName?: string | null;
  sentAt: Date;
}

/**
 * Snapshot of a text message that was sent when completing a task.
 */
export interface TaskCompletionText {
  recipientPhone: string;
  body: string;
  /** The SMS template key used (e.g. `birthdayText`). */
  templateName: string;
  personalMessage: string;
  senderName?: string | null;
  sentAt: Date;
  /** Firestore doc id in the `messages` collection (when known). */
  messageDocId?: string | null;
}

export interface Task {
  id: string;
  title: string;
  notes: string | null;
  event: TaskEvent;
  status: TaskStatus;

  /** Firestore `contacts` document id. */
  contact_id: string;
  leadId?: string | null;

  /**
   * Channels the user plans to reach out on when this task is completed.
   * Empty array (or absent) means "no preference" — the completion dialog
   * will fall back to whatever contact info is available.
   */
  preferredChannels?: TaskChannel[];

  /**
   * How much automation the user opted into when creating the task.
   * Defaults to `partial` for tasks created before this field existed.
   */
  automationLevel?: TaskAutomationLevel;
  /**
   * Marker set by the auto-completion scheduled function so it doesn't run
   * twice on the same task and so the UI can show that this completion came
   * from automation rather than the user.
   */
  autoCompletedAt?: Date | null;

  /** Sales rep responsible for the task; null when unassigned. */
  assignedToId: string | null;
  assignedToName?: string | null;

  dueDate: Date;
  completedAt: Date | null;

  /** Email sent when completing the task (populated by the email/text completion flow). */
  completionEmail?: TaskCompletionEmail | null;
  /** Text message sent when completing the task. */
  completionText?: TaskCompletionText | null;

  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export const TASK_EVENT_LABELS: Record<TaskEvent, string> = {
  birthday: 'Birthday',
  truck_anniversary: 'Truck purchase anniversary',
  lead_lost: 'Lead marked lost',
  follow_up: 'Follow up',
  check_in: 'Check in',
  custom: 'Custom',
};
